import { Tooltip, Typography } from "@mui/material";
import type { TypographyProps } from "@mui/material/Typography";

interface CountryNameProps {
  code: string | null | undefined;
  showFlag?: boolean;
  showName?: boolean;
  fallback?: string;
  variant?: TypographyProps["variant"];
}

let displayNames: Intl.DisplayNames | null = null;
function getDisplayNames(): Intl.DisplayNames | null {
  if (displayNames) return displayNames;
  try {
    displayNames = new Intl.DisplayNames(
      typeof navigator !== "undefined" ? navigator.language : "en",
      { type: "region" },
    );
    return displayNames;
  } catch {
    return null;
  }
}

const ALPHA2 = /^[A-Z]{2}$/;

export function getCountryName(code: string): string {
  const upper = code.toUpperCase();
  if (!ALPHA2.test(upper)) return upper;
  try {
    return getDisplayNames()?.of(upper) ?? upper;
  } catch {
    return upper;
  }
}

export function getCountryFlag(code: string): string {
  const upper = code.toUpperCase();
  if (!ALPHA2.test(upper)) return "";
  return String.fromCodePoint(127397 + upper.charCodeAt(0), 127397 + upper.charCodeAt(1));
}

/** A country as a filter option: its code, labelled "🇳🇴 Norway". */
export function countryOption(code: string): { code: string; label: string } {
  const flag = getCountryFlag(code);
  const name = getCountryName(code);
  return { code, label: flag ? `${flag} ${name}` : name };
}

export default function CountryName({
  code,
  showFlag = true,
  showName = true,
  fallback = "-",
  variant = "inherit",
}: CountryNameProps) {
  if (!code) return <>{fallback}</>;
  const upper = code.toUpperCase();
  const name = getCountryName(upper);
  const flag = showFlag ? getCountryFlag(upper) : "";

  return (
    <Tooltip title={upper}>
      <Typography
        component="span"
        variant={variant}
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}
      >
        {flag && <span aria-hidden>{flag}</span>}
        {showName && <span>{name}</span>}
      </Typography>
    </Tooltip>
  );
}
